import clsx from 'clsx';
import React from 'react';
import Dialog from '../Dialog/Dialog';
import useDialogConfirm from './useDialogConfirm';

/**
 * DialogConfirm — v2 organism.
 *
 * Modal de confirmación construido sobre {@link Dialog} con `isAlert`,
 * `size="s"` y un `iconTitle` decorativo (por defecto `warning`). El `text`
 * se delega al `Dialog` base para cablear `aria-describedby` correctamente;
 * el cuerpo del dialog renderiza `children` o, si no se proveen, la lista
 * opcional de detalles (`detailedList`) como `<ul>/<li>` semántico.
 *
 * Accesibilidad (WCAG 2.1 AA):
 *  - Hereda de `Dialog` v2: focus trap, cierre por `Escape`, `aria-modal`,
 *    `role="alertdialog"` (porque `isAlert`), devolución de foco al trigger,
 *    `aria-labelledby` apuntando al título y `aria-describedby` apuntando al
 *    `text` cuando está presente.
 *  - Cuando `onCancel` no se pasa, el dialog reutiliza `onClose` como
 *    handler de cancelación para garantizar que el botón "Cancel" siempre
 *    tenga una acción válida. Si `hasCancelButton=false`, el botón Cancel se
 *    oculta por completo (Dialog v2 no renderiza el botón cuando `onCancel`
 *    es `undefined`).
 */

export type DialogConfirmProps = Readonly<{
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  children?: React.ReactNode;
  className?: string;
  detailedList?: ReadonlyArray<React.ReactNode>;
  hasCancelButton?: boolean;
  iconName?: string;
  isLoading?: boolean;
  isOpen: boolean;
  onCancel?: () => void;
  onClose: () => void;
  onSuccess?: () => void;
  text?: string;
  title: string;
}>;

function DialogConfirm({
  buttonTextCancel,
  buttonTextSuccess,
  children,
  className,
  detailedList,
  hasCancelButton = true,
  iconName,
  isLoading = false,
  isOpen,
  onCancel,
  onClose,
  onSuccess,
  text,
  title,
}: DialogConfirmProps): React.JSX.Element {
  const {
    hasChildren,
    hasDetailedList,
    resolvedButtonTextCancel,
    resolvedButtonTextSuccess,
    resolvedIconName,
    resolvedOnCancel,
  } = useDialogConfirm({ buttonTextCancel, buttonTextSuccess, children, detailedList, hasCancelButton, iconName, onCancel, onClose });

  const hasBody = hasChildren || hasDetailedList;

  return (
    <Dialog
      buttonTextCancel={resolvedButtonTextCancel}
      buttonTextSuccess={resolvedButtonTextSuccess}
      className={clsx('dialog-confirm', className)}
      iconTitle={resolvedIconName}
      isAlert
      isLoading={isLoading}
      isOpen={isOpen}
      onCancel={resolvedOnCancel}
      onClose={onClose}
      onSuccess={onSuccess}
      size="s"
      text={text}
      title={title}
    >
      {hasBody && (
        <div className="dialog-confirm__content">
          {hasChildren ? (
            children
          ) : (
            <ul className="dialog-confirm__list">
              {detailedList?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li className="dialog-confirm__list-item" key={index}>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}

export default DialogConfirm;
